import { createContext, ReactNode, useContext, useMemo } from "react";
import {
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatProps,
  StatNumberProps,
  StatLabelProps,
  Skeleton,
  SkeletonProps,
} from "@chakra-ui/react";
import { NA } from "components/Shared/EmptyPlaceholder";

export type LabeledValueContextValue = {
  isLoaded: boolean;
  labelProps?: StatLabelProps;
  statProps?: StatProps;
  valueProps?: StatNumberProps;
};

export const labeledValueContext = createContext<LabeledValueContextValue>({
  isLoaded: true,
});

export const labelStyles = {
  color: "magenta.dark",
  textTransform: "uppercase",
  fontSize: ".8em",
  fontWeight: 700,
} as const;

export type LabeledValueProps = StatProps & {
  below?: ReactNode;
  hasSecondaryValue?: boolean;
  helpText?: ReactNode;
  isLarge?: boolean;
  isLoaded?: boolean;
  label: ReactNode;
  labelProps?: StatLabelProps;
  secondaryValue?: ReactNode;
  secondaryValueProps?: SkeletonProps;
  value: ReactNode;
  valuePlaceholder?: ReactNode;
  valueProps?: StatNumberProps;
};

export default function LabeledValue({
  below,
  helpText,
  isLarge = false,
  isLoaded: isLoadedProp,
  label,
  labelProps,
  secondaryValue,
  secondaryValueProps,
  hasSecondaryValue = !!secondaryValue,
  value,
  valuePlaceholder = <NA />,
  valueProps,
  ...rest
}: LabeledValueProps) {
  const {
    isLoaded: isLoadedContext,
    labelProps: labelPropsContext,
    statProps: statPropsContext,
    valueProps: valuePropsContext,
  } = useContext(labeledValueContext);

  const isLoaded =
    typeof isLoadedProp === "boolean" ? isLoadedProp : isLoadedContext;

  return (
    <Stat
      py={3}
      variant="labeledValue"
      data-large={isLarge || undefined}
      {...statPropsContext}
      {...rest}
    >
      <StatLabel {...labelPropsContext} {...labelProps}>
        {label}
      </StatLabel>

      <StatNumber
        display="flex"
        flexFlow="row"
        alignItems="baseline"
        {...valuePropsContext}
        {...valueProps}
      >
        <Skeleton isLoaded={isLoaded} display="inline-block" maxW="100%">
          {value ?? valuePlaceholder}
        </Skeleton>

        {hasSecondaryValue && (
          <Skeleton
            as="small"
            isLoaded={isLoaded}
            display="inline-block"
            fontWeight="light"
            ml={2}
            fontSize=".65em"
            {...secondaryValueProps}
          >
            {secondaryValue ?? <NA />}
          </Skeleton>
        )}
      </StatNumber>

      {helpText && <StatHelpText>{helpText}</StatHelpText>}

      {below}
    </Stat>
  );
}

export type LabeledValueContextProviderProps = LabeledValueContextValue & {
  children: ReactNode;
};

export function LabeledValueContextProvider({
  children,
  isLoaded,
  labelProps,
  statProps,
  valueProps,
}: LabeledValueContextProviderProps) {
  const value = useMemo(
    () => ({ isLoaded, labelProps, statProps, valueProps }),
    [isLoaded, labelProps, statProps, valueProps],
  );
  return (
    <labeledValueContext.Provider value={value}>
      {children}
    </labeledValueContext.Provider>
  );
}
